import {
  QueryClient,
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { ProvinceData } from "./province";
import { axiosInstance } from "@/utils/lib/axios";
import {
  MAX_3_CHARACTERS_MESSAGE,
  MAX_5_CHARACTERS_MESSAGE,
  REQUIRED_MESSAGE,
} from "@/utils/constant";
import { LoaderFunction, LoaderFunctionArgs } from "react-router-dom";
import { Paginated } from "./types";
import { generateParams } from "@/utils/helper/generateParams";
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { MasterParamData } from "./master-param";
import { stringRange } from "@/utils/helper/refinement";
import { AxiosResponse } from "axios";

const cityPayloadSchema = z.object({
  cityCode: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .max(5, { message: MAX_5_CHARACTERS_MESSAGE }),
  cityName: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine(...stringRange(4, 50)),
  province: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
  ftax: z
    .number()
    .nonnegative({ message: "FTAX must be a non-negative number" })
    .or(
      z
        .string()
        .min(1, { message: REQUIRED_MESSAGE })
        .max(3, { message: MAX_3_CHARACTERS_MESSAGE })
        .refine((val) => !isNaN(parseFloat(val)) && parseFloat(val) >= 0, {
          message: "FTAX must be a non-negative number",
        }),
    )
    .pipe(z.coerce.number()),
  fdtAreaHpm: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
  fibernodeAreaHpm: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
  branchHpm: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
});

type CityPayloadSchema = z.infer<typeof cityPayloadSchema>;

type CityData = {
  id: string;
  cityCode: string;
  cityName: string;
  province: ProvinceData;
  ftax: number;
  fdtAreaHpm: MasterParamData;
  fibernodeAreaHpm: MasterParamData;
  branchHpm: MasterParamData;
};

/* ========= *
 * READ city *
 * ========= */

type CitiesQueryKey = DataConfig & {
  cityName?: string;
};
interface CityList extends Paginated {
  content: CityData[];
}
async function getCities(
  queryFunctionContext: QueryFunctionContext,
): Promise<CityList> {
  const { queryKey } = queryFunctionContext;
  const [_, citiesQueryKey] = queryKey;
  const params = generateParams(citiesQueryKey as CitiesQueryKey);

  const response = await axiosInstance.get(`/api/crud/city${params}`);
  return response.data.data;
}

function useCities(
  citiesQueryKey: CitiesQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["cities", citiesQueryKey],
    queryFn: getCities,
  });
}

interface CityQueryKey {
  id: string;
}

async function getCity(
  queryFunctionContext: QueryFunctionContext,
): Promise<CityData> {
  const { queryKey } = queryFunctionContext;
  const [_, cityQueryKey] = queryKey;
  const { id } = cityQueryKey as CityQueryKey;

  const response = await axiosInstance.get(`/api/crud/city/${id}`);
  return response.data.data;
}

function useCity(cityQueryKey: CityQueryKey) {
  const { id } = cityQueryKey;
  return useQuery({
    queryKey: ["cities", { id }],
    queryFn: getCity,
  });
}

function cityLoader(queryClient: QueryClient): LoaderFunction<CityData> {
  return async ({ params }: LoaderFunctionArgs<{ id: string }>) => {
    const city = await queryClient.ensureQueryData({
      queryKey: ["cities", { id: params.id }],
      queryFn: getCity,
    });
    return city;
  };
}

/* =========== *
 * CREATE city *
 * =========== */

async function createCity(data: CityPayloadSchema): Promise<CityData> {
  const response = await axiosInstance.post("/api/crud/city", data);
  return response.data;
}

function useCityCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: CityPayloadSchema }) => createCity(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["cities"],
      });
    },
  });
}

/* =========== *
 * UPDATE city *
 * =========== */

async function editCity(id: string, data: CityPayloadSchema) {
  const response = await axiosInstance.post(`/api/crud/city/${id}`, data);
  return response.data;
}

function useCityEdit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CityPayloadSchema }) =>
      editCity(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["cities"],
      });
    },
  });
}

/* =========== *
 * DELETE city *
 * =========== */

async function deleteCity(id: string) {
  const response = await axiosInstance.delete(`/api/crud/city/${id}`);
  return response.data;
}

function useCityDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteCity(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["cities"],
      });
    },
  });
}

async function deleteBulkCity(ids: string[]) {
  return await Promise.all(ids.map((id) => deleteCity(id)));
}

function useCityBulkDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids }: { ids: string[] }) => deleteBulkCity(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["cities"],
      });
    },
  });
}

// Utility to poll for media completion
async function pollMediaStatus(
  id: string,
): Promise<{ status: string; path?: string; originalFileName?: string }> {
  const response: AxiosResponse = await axiosInstance.get(
    `/api/crud/media/${id}`,
  );
  return response.data.data;
}

// Wait for media completion with a polling mechanism
async function waitForMediaCompletion(
  id: string,
  interval = 2000,
  pollFn = pollMediaStatus, // Dependency injection for easier testing
): Promise<{ status: string; path?: string; originalFileName?: string }> {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { status, path, originalFileName } = await pollFn(id);

    if (status === "SUCCESS" || status === "ERROR") {
      return { status, path, originalFileName };
    }

    await new Promise((resolve) => setTimeout(resolve, interval));
  }
}

// Utility to handle file download
function downloadFile(
  blobData: Blob,
  fileName: string,
  createObjectURL = window.URL.createObjectURL,
): void {
  const url = createObjectURL(blobData);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Export city data
async function exportCity(
  config: DataConfig = {},
  waitFn = waitForMediaCompletion, // Dependency injection for easier testing
  downloadFn = downloadFile, // Dependency injection for easier testing
): Promise<{ status: string }> {
  const createResponse = await axiosInstance.get("/api/crud/city/excel", {
    params: { ...config },
  });

  const { status, path, originalFileName } = await waitFn(
    createResponse.data.data.id,
  );

  if (status === "SUCCESS") {
    const downloadResponse = await axiosInstance.get(
      "/api/crud/media/download",
      {
        params: { path, fileName: originalFileName },
        responseType: "blob",
      },
    );

    downloadFn(
      new Blob([downloadResponse.data]),
      originalFileName ?? "city-list.xlsx",
    );
  }

  return { status };
}

// React Query hook for exporting city
function useExportCity() {
  return useMutation({
    mutationFn: async ({ config }: { config?: DataConfig }) =>
      exportCity(config),
  });
}

export type { CityData, CityPayloadSchema };
export {
  cityPayloadSchema,
  useCities,
  useCity,
  cityLoader,
  useCityCreate,
  useCityEdit,
  useCityDelete,
  useCityBulkDelete,
  pollMediaStatus,
  waitForMediaCompletion,
  downloadFile,
  exportCity,
  useExportCity,
};
